'use client';

/**
 * Klaviyo Client-Side Utilities
 *
 * Browser-only utilities for Klaviyo event tracking and user identification.
 * These functions safely handle cases where the Klaviyo script hasn't loaded yet.
 *
 * Features:
 * - User identification with email and custom properties
 * - Generic event tracking with automatic queuing
 * - Specialized e-commerce tracking (Started Checkout)
 * - SessionStorage integration for cross-page identity persistence
 *
 * @example
 * ```ts
 * // Identify user when they enter email
 * klaviyoIdentify('user@example.com', { plan: 'premium' });
 *
 * // Track custom events
 * klaviyoTrack('Button Clicked', { button_name: 'Sign Up' });
 *
 * // Track checkout started
 * trackStartedCheckout({
 *   value: 99.99,
 *   items: [{ ProductID: '123', ProductName: 'Product', ... }],
 *   checkoutURL: 'https://example.com/checkout'
 * });
 * ```
 */

// Helper to safely access the klaviyo object
// The script may not be loaded yet on first render
function getKlaviyo() {
  if (typeof window === 'undefined') return null;
  return window.klaviyo ?? null;
}

// Retrieve the stored _kx parameter or email from sessionStorage
export function getStoredIdentity() {
  if (typeof window === 'undefined') return {};
  return {
    kx: sessionStorage.getItem('klaviyo_kx'),
    email: sessionStorage.getItem('klaviyo_email'),
  };
}

/**
 * Get the Klaviyo anonymous ID from cookie or localStorage
 * This ID is used for tracking anonymous visitors before they are identified
 * Klaviyo stores this as __kla_id in cookies and _kla_id in localStorage
 *
 * @returns The anonymous ID if available, null otherwise
 */
export function getAnonymousId(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    // First try to get from cookie (__kla_id)
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === '__kla_id') {
        return decodeURIComponent(value);
      }
    }

    // Fallback to localStorage (_kla_id) - older implementation
    const localStorageId = localStorage.getItem('_kla_id');
    if (localStorageId) {
      return localStorageId;
    }

    return null;
  } catch (error) {
    console.error('Error getting Klaviyo anonymous ID:', error);
    return null;
  }
}

/**
 * Capture and store the _kx parameter from URL query string
 * Call this on page load to ensure attribution works correctly
 *
 * @example
 * ```ts
 * useEffect(() => {
 *   captureKlaviyoParams();
 * }, []);
 * ```
 */
export function captureKlaviyoParams() {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const kx = params.get('_kx');

    if (kx) {
      sessionStorage.setItem('klaviyo_kx', kx);

      // Log for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('[Klaviyo] Captured _kx parameter:', kx);
      }
    } else if (process.env.NODE_ENV === 'development') {
      console.log('[Klaviyo] No _kx parameter in URL');
      const storedKx = sessionStorage.getItem('klaviyo_kx');
      if (storedKx) {
        console.log(
          '[Klaviyo] Using stored _kx from sessionStorage:',
          storedKx,
        );
      }
    }
  } catch (error) {
    console.error('[Klaviyo] Error capturing params:', error);
  }
}

// —— IDENTIFY ——
// Call this whenever you know the user's email (e.g., after they fill a form field)
// The Klaviyo script automatically reads __kla_id cookie and handles the merge
export function klaviyoIdentify(
  email: string,
  props: Record<string, unknown> = {},
) {
  const k = getKlaviyo();
  if (!k) {
    console.warn('[Klaviyo] Script not loaded yet, cannot identify user');
    return;
  }

  // Get stored _kx parameter for attribution
  const { kx } = getStoredIdentity();
  const anonymousId = getAnonymousId(); // For logging only

  const identifyPayload = {
    $email: email,
    // Include _kx for email click attribution
    ...(kx && { _kx: kx }),
    // Other properties
    ...props,
  };

  // The Klaviyo SDK automatically includes __kla_id from cookie
  // No need to manually pass $anonymous - it handles the merge internally
  k.identify(identifyPayload);

  // Store email in sessionStorage for server-side calls
  sessionStorage.setItem('klaviyo_email', email);

  // Log for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('[Klaviyo] User identified (merge handled automatically):', {
      email,
      cookie_kla_id: anonymousId,
      _kx: kx,
      payload: identifyPayload,
    });
  }
}

// —— TRACK ——
// Generic event tracking - client side only
export function klaviyoTrack(
  event: string,
  props: Record<string, unknown> = {},
) {
  const k = getKlaviyo();

  // Include _kx parameter if available for proper attribution
  const { kx } = getStoredIdentity();
  const enrichedProps = {
    ...props,
    ...(kx && { _kx: kx }),
  };

  if (!k) {
    // If script not loaded yet, queue the call
    if (typeof window !== 'undefined') {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['track', event, enrichedProps]);
    }
    return;
  }
  k.track(event, enrichedProps);
  // console.log('Klaviyo client-side event tracked:', {
  //   event,
  //   properties: enrichedProps,
  // });
}

// —— STARTED CHECKOUT ——
/**
 * CLIENT-SIDE ONLY tracking for when user lands on checkout page
 * This is intentionally client-side to avoid duplicate events
 *
 * For SERVER-SIDE checkout tracking (webhooks, etc), use trackKlaviyoEvent() action directly
 */
export function trackStartedCheckout(data: {
  value: number;
  items: Array<{
    ProductID: string;
    ProductName: string;
    Quantity: number;
    ItemPrice: number;
    ImageURL: string;
    ProductURL?: string;
  }>;
  checkoutURL: string;
}) {
  const itemNames = data.items.map((i) => i.ProductName);

  klaviyoTrack('Started Checkout', {
    $value: data.value,
    ItemNames: itemNames,
    CheckoutURL: data.checkoutURL,
    Items: data.items,
  });
}

// —— PLACED ORDER ——
/**
 * SERVER-SIDE ONLY tracking for completed purchases
 * This ensures reliable tracking even if user closes browser
 * Call this from server-side code (API routes, webhooks) with customer email
 *
 * For CLIENT-SIDE order tracking, use trackKlaviyoEventFromClient() instead
 *
 * @deprecated Use server-side trackKlaviyoEvent() action directly for production
 */
export function trackPlacedOrder(data: {
  value: number;
  orderId: string;
  items: Array<{
    ProductID: string;
    ProductName: string;
    Quantity: number;
    ItemPrice: number;
    ImageURL: string;
    ProductURL?: string;
  }>;
}) {
  const itemNames = data.items.map((i) => i.ProductName);

  klaviyoTrack('Placed Order', {
    $value: data.value,
    OrderId: data.orderId,
    ItemNames: itemNames,
    Items: data.items,
  });
}

// —— SERVER-SIDE EVENT TRACKING (via Server Action) ——
/**
 * Track Klaviyo events server-side from client components
 * This is a client-side wrapper that calls the server action
 *
 * ✅ AUTOMATICALLY includes _kx parameter for proper attribution
 *
 * RECOMMENDED FOR:
 * - Placed Order (guaranteed tracking, no client-side issues)
 * - Preorder flows
 * - Critical conversion events
 * - CTAs that need reliable attribution
 *
 * @example
 * ```ts
 * // Simple usage - _kx is automatically included
 * await trackKlaviyoEventFromClient({
 *   email: 'user@example.com',
 *   eventName: 'Placed Order',
 *   properties: {
 *     order_id: '12345',
 *   },
 *   value: 99.99,
 * });
 * ```
 */
export async function trackKlaviyoEventFromClient(data: {
  email: string;
  eventName: string;
  properties?: Record<string, unknown>;
  value?: number;
}) {
  // Get _kx from sessionStorage for attribution
  const { kx } = getStoredIdentity();

  // Automatically include _kx in properties if available
  const enrichedData = {
    ...data,
    properties: {
      ...data.properties,
      ...(kx && { _kx: kx }),
    },
  };

  // Dynamic import to avoid bundling server action in all client components
  const { trackKlaviyoEvent } = await import('./actions');
  return trackKlaviyoEvent(enrichedData);
}
